import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Container,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  TableFooter,
  TablePagination,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import GppBadIcon from '@mui/icons-material/GppBad';
import GppGoodIcon from '@mui/icons-material/GppGood';
import useMediaQuery from '@mui/material/useMediaQuery';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
//--------------------------------------------------------
import { HttpErrorHandler, ErrorUI } from '../../utils/ErrorHandler';
import { PaginationAPI, theme } from '../../utils/TableStyles';
import { DefaultContainedButton } from '../../utils/Buttons';
import { LoadingUI } from '../../utils/LayoutStyles';
import API from '../../api';

export default function Newsletter() {
  // general
  const navigate = useNavigate();
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [errorMessage, setErrorMessage] = useState('');
  const [apiResponseData, setApiResponseData] = useState({});
  const [apiCalled, setApiCalled] = useState(false);
  const [rowsApiCalled, setRowsApiCalled] = useState(false);
  const [currentProfile, setCurrentProfile] = useState('');

  // newsletters data
  const [newsletters, setNewsletters] = useState([]);
  const [activeNewsletters, setActiveNewsletters] = useState([]);
  const [createdNewsletters, setCreatedNewsletters] = useState([]);
  const [closedNewsletters, setClosedNewsletters] = useState([]);
  const [displayedNewsletters, setDisplayedNewsletters] = useState([]);

  /* ------------------------------- Filter Tabs ------------------------------ */
  const [filterValue, setFilterValue] = useState(0);
  const handleChangeFilterTab = (event, newValue) => {
    setFilterValue(newValue);
  };

  // call initial function
  useEffect(() => {
    getActiveNewsletters();
    getCreatedNewsletters();
    getClosedNewsletters();
    getNewsletters();
  }, []);

  // Get Newsletters
  const getNewsletters = () => {
    API.get('/newsletters')
      .then((response) => {
        setNewsletters(response.data);
        setDisplayedNewsletters(response.data.data);
        setApiResponseData(response.data.data);
        setApiCalled(true);
        setRowsApiCalled(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
        setApiCalled(true);
      });
  };

  // show newsletters by tab value
  useEffect(() => {
    switch (filterValue) {
      case 0:
        setApiResponseData(newsletters);
        setDisplayedNewsletters(newsletters.data);
        break;
      case 1:
        setApiResponseData(activeNewsletters);
        setDisplayedNewsletters(activeNewsletters.data);
        break;
      case 2:
        setApiResponseData(createdNewsletters);
        setDisplayedNewsletters(createdNewsletters.data);
        break;
      case 3:
        setApiResponseData(closedNewsletters);
        setDisplayedNewsletters(closedNewsletters.data);
        break;
      default:
        break;
    }
  }, [filterValue]);

  // get active newsletter
  const getActiveNewsletters = (display) => {
    API.get(`/newsletters/status?val=active`).then((response) => {
      setActiveNewsletters(response.data);
      setRowsApiCalled(true);

      if (display) {
        setDisplayedNewsletters(response.data.data);
      }
    });
  };

  // get created newsletters
  const getCreatedNewsletters = (display) => {
    API.get(`/newsletters/status?val=created`).then((response) => {
      setCreatedNewsletters(response.data);
      setRowsApiCalled(true);

      if (display) {
        setDisplayedNewsletters(response.data.data);
      }
    });
  };

  // get closed newsletters
  const getClosedNewsletters = (display) => {
    API.get(`/newsletters/status?val=inactive`).then((response) => {
      setClosedNewsletters(response.data);
      setRowsApiCalled(true);

      if (display) {
        setDisplayedNewsletters(response.data.data);
      }
    });
  };

  /* ------------------------------- Pagination ------------------------------- */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const [sliceStartDisplay, setSliceStartDisplay] = useState(0);
  const [sliceEndDisplay, setSliceEndDisplay] = useState(0);

  // // empty rows
  const emptyRows =
    page > 0
      ? rowsPerPage === 5 && page % 2 === 0
        ? Math.max(0, 1 * rowsPerPage - newsletters.length)
        : rowsPerPage === 5 && page % 2 !== 0
        ? Math.max(0, 1 * rowsPerPage + 5 - newsletters.length)
        : rowsPerPage === 10
        ? Math.max(0, 1 * rowsPerPage - newsletters.length)
        : 0
      : 0;

  // dynamically displays users number
  useEffect(() => {
    if (rowsPerPage === 10) {
      setSliceStartDisplay(0);
      setSliceEndDisplay(10);
    } else if (rowsPerPage === 5) {
      if (page % 2 === 0) {
        setSliceStartDisplay(0);
        setSliceEndDisplay(5);
      } else {
        setSliceStartDisplay(5);
        setSliceEndDisplay(10);
      }
    }
  }, [rowsPerPage, page]);

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);

    if (filterValue === 0) {
      getNewsletters();
    } else if (filterValue === 1) {
      getActiveNewsletters(true);
    } else if (filterValue === 2) {
      getCreatedNewsletters(true);
    } else {
      getClosedNewsletters();
    }

    setRowsApiCalled(false);
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <>
      <Helmet>
        <title>Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* <Stack direction="row" justifyContent="end" alignItems="center" sx={{ paddingBottom: '24px' }}>
          {apiCalled && (
            <DefaultContainedButton onClick={() => navigate(`/admin/newsletter/new`)}>
              Crea Newsletter
            </DefaultContainedButton>
          )}
        </Stack> */}

        {apiCalled ? (
          errorMessage.length === 0 ? (
            <Card>
              <Tabs
                value={filterValue}
                onChange={handleChangeFilterTab}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="secondary"
                indicatorColor="secondary"
                sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
              >
                <Tab
                  disableRipple
                  icon={
                    <Typography
                      sx={{
                        backgroundColor: filterValue === 0 ? '#000000' : '#edeff1',
                        borderRadius: '6px',
                        color: filterValue === 0 ? '#ffffff' : '#637381',
                        padding: '2px 8px',
                        fontSize: '1em',
                        fontWeight: 'bold',
                      }}
                    >
                      {/* {newsletters.total} */} 0
                    </Typography>
                  }
                  iconPosition="end"
                  label="Tutte"
                />
                <Tab
                  disableRipple
                  icon={
                    <Typography
                      sx={{
                        backgroundColor: filterValue === 1 ? '#22c55e' : '#dbf6e5',
                        borderRadius: '6px',
                        color: filterValue === 1 ? '#dbf6e5' : '#118d57',
                        padding: '2px 8px',
                        fontSize: '1em',
                        fontWeight: 'bold',
                      }}
                    >
                      {/* {activeNewsletters.total} */}0
                    </Typography>
                  }
                  iconPosition="end"
                  label="Attive"
                />
                <Tab
                  disableRipple
                  icon={
                    <Typography
                      sx={{
                        backgroundColor: filterValue === 2 ? '#00b8d9' : '#d6f4f9',
                        borderRadius: '6px',
                        color: filterValue === 2 ? '#ffffff' : '#1177a4',
                        padding: '2px 8px',
                        fontSize: '1em',
                        fontWeight: 'bold',
                      }}
                    >
                      {/* {createdNewsletters.total} */}0
                    </Typography>
                  }
                  iconPosition="end"
                  label="Create"
                />
                <Tab
                  disableRipple
                  icon={
                    <Typography
                      sx={{
                        backgroundColor: filterValue === 3 ? '#ffa700' : '#fff1d6',
                        borderRadius: '6px',
                        color: filterValue === 3 ? '#ffffff' : '#b76e00',
                        padding: '2px 8px',
                        fontSize: '1em',
                        fontWeight: 'bold',
                      }}
                    >
                      0
                    </Typography>
                  }
                  iconPosition="end"
                  label="In Elaborazione"
                />
                <Tab
                  disableRipple
                  icon={
                    <Typography
                      sx={{
                        backgroundColor: filterValue === 4 ? '#ff5630' : '#ffe4de',
                        borderRadius: '6px',
                        color: filterValue === 4 ? '#ffffff' : '#b71d18',
                        padding: '2px 8px',
                        fontSize: '1em',
                        fontWeight: 'bold',
                      }}
                    >
                      {/* {closedNewsletters.total} */}0
                    </Typography>
                  }
                  iconPosition="end"
                  label="Terminate"
                />
              </Tabs>

              <TableContainer sx={{ padding: '24px 24px 6px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Oggetto</TableCell>
                      <TableCell>Data Creazione</TableCell>
                      {is600Screen && <TableCell>Filtro Applicato</TableCell>}
                      <TableCell align="center">Stato</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  {newsletters.length > 0 ? (
                    <TableBody>
                      {rowsApiCalled ? (
                        newsletters.slice(sliceStartDisplay, sliceEndDisplay).map((newsletter) => (
                          <TableRow key={newsletter.id}>
                            <TableCell>{newsletter.content[0].subject}</TableCell>
                            <TableCell>{dayjs(newsletter.creation_date).format('DD/MM/YYYY')}</TableCell>
                            {is600Screen && (
                              <TableCell>
                                <Typography
                                  sx={{
                                    height: '24px',
                                    maxWidth: '110px',
                                    borderRadius: '6px',
                                    padding: '0 6px',
                                    color: (() => {
                                      switch (newsletter.selection.name) {
                                        case 'ALL':
                                          return '#118d57';
                                        case 'ENTITY':
                                          return '#2f4ebf';
                                        case 'OFFICE':
                                          return '#b71d18';
                                        case 'ADVANCED':
                                          return '#b76e00';
                                        default:
                                          return 'black';
                                      }
                                    })(),
                                    backgroundColor: (() => {
                                      switch (newsletter.selection.name) {
                                        case 'ALL':
                                          return '#d3efdf';
                                        case 'ENTITY':
                                          return '#d8e8f7';
                                        case 'OFFICE':
                                          return '#f7ddd8';
                                        case 'ADVANCED':
                                          return '#f7ead0';
                                        default:
                                          return '#b0b0b0';
                                      }
                                    })(),
                                    textAlign: 'center',
                                    fontWeight: '700',
                                  }}
                                >
                                  {(() => {
                                    switch (newsletter.selection.name) {
                                      case 'ALL':
                                        return 'Nessuno';
                                      case 'ENTITY':
                                        return 'Entità';
                                      case 'OFFICE':
                                        return 'Carica';
                                      case 'ADVANCED':
                                        return 'Avanzato';
                                      default:
                                        return '/';
                                    }
                                  })()}
                                </Typography>
                              </TableCell>
                            )}

                            <TableCell align="center">
                              {(() => {
                                switch (newsletter.status_name) {
                                  case 'ACTIVE':
                                    return (
                                      <Tooltip title="Attiva" arrow disableInteractive placement="right">
                                        <GppGoodIcon sx={{ color: '#00a600', fontSize: '2.3em' }} />
                                      </Tooltip>
                                    );
                                  case 'INACTIVE':
                                    return (
                                      <Tooltip title="Disattiva" arrow disableInteractive placement="right">
                                        <GppBadIcon sx={{ color: 'red', fontSize: '2.3em' }} />
                                      </Tooltip>
                                    );
                                  case 'IN WORK':
                                    return (
                                      <Tooltip title="In Elaborazione" arrow disableInteractive placement="right">
                                        <SafetyCheckIcon sx={{ color: 'orange', fontSize: '2.3em' }} />
                                      </Tooltip>
                                    );
                                  case 'CREATED':
                                    return (
                                      <Tooltip title="Creata" arrow disableInteractive placement="right">
                                        <PrivacyTipIcon sx={{ color: '#385de5', fontSize: '2.1em' }} />
                                      </Tooltip>
                                    );
                                  default:
                                    return 'Stato';
                                }
                              })()}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                onClick={() => {
                                  navigate('/admin/newsletter/newsletter-details', {
                                    state: { newsID: newsletter.id },
                                  });
                                }}
                              >
                                <KeyboardArrowRightIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9}>{LoadingUI()}</TableCell>
                        </TableRow>
                      )}

                      {emptyRows > 0 && (
                        <TableRow style={{ height: 47 * emptyRows }}>
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Nessuna newsletter disponibile
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  <TableFooter>
                    <TableRow>
                      <ThemeProvider theme={theme}>
                        <TablePagination
                          sx={{ border: 0 }}
                          rowsPerPageOptions={[]}
                          colSpan={5}
                          count={apiResponseData.total}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          labelRowsPerPage="Righe per Pagina"
                          ActionsComponent={(props) => (
                            <PaginationAPI
                              {...props}
                              elements={10}
                              apiResponseData={apiResponseData}
                              setApiResponseData={setApiResponseData}
                              lastPage={apiResponseData.last_page}
                            />
                          )}
                        />
                      </ThemeProvider>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            // error handler
            ErrorUI(errorMessage)
          )
        ) : (
          // loading
          LoadingUI()
        )}
      </Container>

      {/* <Container maxWidth="xl">
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box component="img" width="70%" src="/assets/images/img/wip.png" />
        </Box>
      </Container> */}
    </>
  );
}
